/**
 * Admin Panel Component
 * Manage users and companies (Admin only)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { clearAuthData, isAdmin } from '../utils/auth';
import UserManagement from './UserManagement';
import CompanyManagement from './CompanyManagement';
import ParentCompanyManagement from './ParentCompanyManagement';
import TransactionManagement from './TransactionManagement';
import LoadDataManagement from './LoadDataManagement';
import '../styles/Admin.css';

function Admin() {
  const [activeTab, setActiveTab] = useState('users');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Verify user is admin
    if (!isAdmin()) {
      navigate('/dashboard');
    }
    // Load companies for transaction management
    loadCompanies();
  }, [navigate]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    window.location.href = '/login';
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="header-left">
          <h1>Admin Panel</h1>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            Dashboard
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-tabs">
          <button
            className={activeTab === 'users' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button
            className={activeTab === 'parent-companies' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('parent-companies')}
          >
            Parent Companies
          </button>
          <button
            className={activeTab === 'companies' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('companies')}
          >
            Companies
          </button>
          <button
            className={activeTab === 'transactions' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('transactions')}
          >
            Transactions
          </button>
          <button
            className={activeTab === 'load-data' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('load-data')}
          >
            Load Data
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'parent-companies' && <ParentCompanyManagement />}
          {activeTab === 'companies' && <CompanyManagement />}
          {activeTab === 'transactions' && (
            <div className="transaction-admin">
              <div className="company-selector-row">
                <label>Select Company:</label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="company-select"
                >
                  <option value="">-- Select a Company --</option>
                  {companies.map(company => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
              <TransactionManagement
                companyId={selectedCompanyId}
                companyName={companies.find(c => c.company_id === selectedCompanyId)?.company_name || ''}
              />
            </div>
          )}
          {activeTab === 'load-data' && (
            <LoadDataManagement companies={companies} />
          )}
        </div>
      </main>
    </div>
  );
}

export default Admin;
